import React, { useState, useCallback, useRef } from 'react';
import { Button, Col, Form, Row, Select, Typography } from 'antd';
import { debounce } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { PopupConfirm } from '@fcomponents/shared/popup-confirm/PopupConfirm';
import { SelectionProcessesService } from '@frontend/src/core/services/selection_processes_service';
import { getAssessmentsBy } from '@frontend/src/core/services/assessment_service';
import styles from './HiringAssessmentsPanel.module.scss';

const service = new SelectionProcessesService();

export const HiringAssessmentsPanel = ({
  initialAssessments,
  value,
  selectionProcessId,
  readonly,
}) => {
  const [assessments, setAssessments] = useState(initialAssessments);
  const [isLoadingAssessments, setIsLoadingAssessments] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const currentPage = useRef(1);
  const assessmentId = useRef();

  const onScroll = useCallback(
    (target) => {
      const { scrollTop, offsetHeight, scrollHeight } = target;
      const shouldLoad = scrollTop + offsetHeight === scrollHeight;
      if (shouldLoad && !isLoadingAssessments) {
        target.scrollTo(0, scrollHeight);
        currentPage.current = currentPage.current + 1;
        setIsLoadingAssessments(true);
        getAssessmentsBy({
          status: 'active',
          selection_process_id: selectionProcessId,
          page: currentPage.current,
          item: 10,
        })
          .then(({ data }) => {
            setAssessments((prevData) => prevData.concat(data));
          })
          .finally(() => setIsLoadingAssessments(false));
      }
    },
    [isLoadingAssessments]
  );

  const onSearch = (search) => {
    setIsLoadingAssessments(true);
    getAssessmentsBy({ page: 1, status: 'active', selection_process_id: selectionProcessId, ...(!!search && { search }) })
      .then(({ data }) => setAssessments(data))
      .finally(() => setIsLoadingAssessments(false));
  };

  const onSelect = (id) => {
    assessmentId.current = id;
  };

  const debouncedSearch = useCallback(debounce(onSearch, 650), []);

  const onConfirm = () => {
    service.updateById(
      selectionProcessId,
      {
        assessment_id: assessmentId.current,
      },
      () => setShowConfirm(false)
    );
  };

  if (!assessments?.length) {
    return null;
  }

  return (
    <>
      <PopupConfirm
        cancelText="Cancelar"
        open={showConfirm}
        title="Cambiar tipo de Evaluación / Scorecard"
        okText="Aceptar"
        onCancel={() => setShowConfirm(false)}
        onOk={onConfirm}
      >
        <Typography.Text>
          ¿Esta evaluación ya presenta resultados estas seguro de que deseas
          modificarla? Al realizar esta acción se eliminaran todos los
          resultados previos.
        </Typography.Text>
      </PopupConfirm>
      <Typography.Paragraph>
        Selecciona el tipo de evaluación que se calificara en este proceso.
      </Typography.Paragraph>
      <Row
        className={styles.panel}
        type="flex"
        align="middle"
        dir="row"
        gutter={5}
      >
        <Col>
          <FontAwesomeIcon icon={faInfoCircle} />
        </Col>
        <Col span={23}>
          <p>
            Al activar esta nueva funcionalidad dentro de este proceso, se
            perderán todos los resultados previos obtenidos en calificaciones.
          </p>
        </Col>
      </Row>
      <Form.Item label="Seleccionar evaluación">
        <Select
          showSearch
          filterOption={false}
          disabled={readonly}
          loading={isLoadingAssessments}
          placeholder="Selecciona un tipo"
          defaultValue={value || undefined}
          onSearch={debouncedSearch}
          onSelect={onSelect}
          onPopupScroll={({ target }) => onScroll(target)}
        >
          {assessments?.filter((item) => (item.deleted === false && item.status === "active") || item.id === value)?.map(({ id, name }) => (
            <Select.Option value={id}>{name.es}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Row type="flex" justify="end">
        <Col>
          <Button type="primary" onClick={() => setShowConfirm(true)}>
            Guardar
          </Button>
        </Col>
      </Row>
    </>
  );
};
